import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMoreItems } from '../../redux/actions/itemActions';
import LoadingSpinner from '../common/LoadingSpinner';

interface Props {
  spacerMarginTop?: string;
}

/**
 * PaginationClicker component with infinite scroll functionality
 * 
 * This component creates an observer that watches for when the user
 * scrolls near the bottom of the product list and automatically
 * loads more products.
 */
const PaginationClicker: React.FC<Props> = ({ spacerMarginTop }) => {
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  
  // Get required state from Redux
  const items = useSelector((state: ReduxState) => state.items);
  const search = useSelector((state: ReduxState) => state.search);
  const categories = useSelector((state: ReduxState) => state.categories);
  
  const {
    currentPageNumber,
    currentItemId,
    totalPages,
    isHalfThePriceClicked,
    sortTypeName,
    paginationInfo
  } = items;
  
  // Check if there are more products to load
  const hasMoreProducts = 
    (paginationInfo?.hasNextPage || false) && 
    (currentPageNumber !== totalPages);

  // Memoize the loadMoreItems function to prevent unnecessary re-renders
  const loadMoreItemsCallback = useCallback(async () => {
    if (isLoading || !hasMoreProducts) return;
    
    try {
      setIsLoading(true);
      
      await dispatch(
        getMoreItems(
          search.searchTerm,
          categories.selectedNode?.crawler_id,
          currentPageNumber + 1,
          currentItemId,
          isHalfThePriceClicked,
          sortTypeName
        )
      );
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, isLoading, hasMoreProducts, search.searchTerm, categories.selectedNode?.crawler_id, 
      currentPageNumber, currentItemId, isHalfThePriceClicked, sortTypeName]);

  useEffect(() => {
    // If we don't have more products or we're already loading, don't set up observer
    if (!hasMoreProducts || isLoading) return;

    // Create IntersectionObserver to detect when user scrolls to the bottom
    const observer = new IntersectionObserver(
      (entries) => {
        // If the trigger element is intersecting (visible)
        if (entries[0].isIntersecting) {
          loadMoreItemsCallback();
        }
      },
      {
        // Set a threshold of 0.1 - trigger when 10% of the element is visible
        threshold: 0.1,
        // Set a margin to load before the user reaches the very bottom
        rootMargin: '0px 0px 200px 0px'
      }
    );

    // Capture the current value of the ref
    const currentLoaderRef = loaderRef.current;

    // Start observing the loader element
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    // Clean up
    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [hasMoreProducts, isLoading, currentPageNumber, loadMoreItemsCallback]);



  // Don't render anything if there are no more products to load
  if (!hasMoreProducts && !isLoading) {
    return null;
  }

  return (
    <div
      ref={loaderRef}
      className="u-w-all u-flex u-justify-center u-pv--24 u-align-center u-relative"
      style={{ bottom: 0, marginTop: spacerMarginTop }}
    >
      {isLoading && (
        <div className="u-text-center">
          <LoadingSpinner size="medium" color="#FF00B9" />
        </div>
      )}
    </div>
  );
}

export default PaginationClicker;
